import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './stores/authStore';
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/shared/ProtectedRoute';
import ModuleGuard from './components/shared/ModuleGuard';
import OnboardingGate from './components/shared/OnboardingGate';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Lazy-loaded module pages (code-splitting)
const BlogHub = lazy(() => import('./pages/Blog'));
const Pages = lazy(() => import('./pages/Pages'));
const SEO = lazy(() => import('./pages/SEO'));
const DnaPage = lazy(() => import('./pages/DNA'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const ContactSubmissions = lazy(() => import('./pages/ContactSubmissions'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const VisualEditor = lazy(() => import('./pages/VisualEditor'));
const Media = lazy(() => import('./pages/Media'));
const AdsPage = lazy(() => import('./pages/Ads'));
const AdsBeginnerWizard = lazy(() => import('./pages/Ads/Wizards/BeginnerCampaignWizard'));
const MetaAdsPage = lazy(() => import('./pages/MetaAds'));
const AdsLPClusters = lazy(() => import('./pages/AdsLPClusters'));
const MetaAdsInsights = lazy(() => import('./pages/MetaAds/Insights'));
const MetaAdRadar = lazy(() => import('./pages/MetaAds/AdRadar'));
const MetaAdsAudiences = lazy(() => import('./pages/MetaAds/Audiences'));
const MetaAdsPixel = lazy(() => import('./pages/MetaAds/Pixel'));
const MetaAdsLeadForms = lazy(() => import('./pages/MetaAds/LeadForms'));
const MetaAdsRules = lazy(() => import('./pages/MetaAds/Rules'));
const MetaAdsCatalog = lazy(() => import('./pages/MetaAds/ProductCatalog'));
const MetaAdsMessaging = lazy(() => import('./pages/MetaAds/MessagingAds'));
const MetaAdsIgShopping = lazy(() => import('./pages/MetaAds/InstagramShopping'));
const CrossChannel = lazy(() => import('./pages/CrossChannel'));
const Keywords = lazy(() => import('./pages/Keywords/KeywordsRedesign'));
const MarketingAcademy = lazy(() => import('./pages/MarketingAcademy'));
const Content = lazy(() => import('./pages/Content'));
const SocialMedia = lazy(() => import('./pages/SocialMedia'));
const Newsletter = lazy(() => import('./pages/Newsletter'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Kontakte = lazy(() => import('./pages/Kontakte'));
const SystemPage = lazy(() => import('./pages/System'));
const LinkHealth = lazy(() => import('./pages/Links'));
const AEO = lazy(() => import('./pages/AEO'));
const Listings = lazy(() => import('./pages/Listings'));
const BrandHub = lazy(() => import('./pages/BrandHub'));
const CookieBanner = lazy(() => import('./pages/CookieBanner'));
const Analytics = lazy(() => import('./pages/Analytics'));
const AttributionDashboard = lazy(() => import('./pages/Analytics/AttributionDashboard'));
const Inbox = lazy(() => import('./pages/Inbox'));
const ContentPackage = lazy(() => import('./pages/ContentPackage'));
const Today = lazy(() => import('./pages/Today'));
const LegacyDashboard = lazy(() => import('./pages/LegacyDashboard'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const CampaignDetail = lazy(() => import('./pages/Campaigns/CampaignDetail'));
const NewCampaign = lazy(() => import('./pages/Campaigns/NewCampaign'));
const MultiChannelPage = lazy(() => import('./pages/MultiChannel'));
const CampaignWizardPage = lazy(() => import('./components/CampaignWizard'));
const Clusters = lazy(() => import('./pages/Clusters'));
const ContentStrategy = lazy(() => import('./pages/ContentStrategy'));
const ContentPlan = lazy(() => import('./pages/ContentPlan'));
const LivingCampaigns = lazy(() => import('./pages/LivingCampaigns'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <LoadingSpinner />
    </div>
  );
}

export default function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Visual Editor — full-screen, outside AppLayout */}
      <Route
        path="/pages/:id/visual"
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageFallback />}>
              <VisualEditor />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={
          <OnboardingGate>
            <Suspense fallback={<PageFallback />}><Today /></Suspense>
          </OnboardingGate>
        } />

        <Route path="legacy-dashboard" element={
          <OnboardingGate>
            <Suspense fallback={<PageFallback />}><LegacyDashboard /></Suspense>
          </OnboardingGate>
        } />

        <Route path="inbox" element={
          <Suspense fallback={<PageFallback />}><Inbox /></Suspense>
        } />

        {/* Campaigns ("Ziele") — Core, kein ModuleGuard */}
        <Route path="campaigns" element={
          <Suspense fallback={<PageFallback />}><Campaigns /></Suspense>
        } />
        {/* Neue Kampagne: Brain-Modul liefert Ideen (Wave M2) */}
        <Route path="campaigns/new" element={
          <ModuleGuard module="brain">
            <Suspense fallback={<PageFallback />}><NewCampaign /></Suspense>
          </ModuleGuard>
        } />
        {/* Campaign-Wizard (Wave M3) — 7-Step-Flow nach Idee-Accept */}
        <Route path="campaigns/new/wizard" element={
          <ModuleGuard module="brain">
            <Suspense fallback={<PageFallback />}><CampaignWizardPage /></Suspense>
          </ModuleGuard>
        } />
        <Route path="campaigns/:id" element={
          <Suspense fallback={<PageFallback />}><CampaignDetail /></Suspense>
        } />

        {/* Living Campaign Engine (Wave 4-7) — Cockpit fuer Mikro-Funnel + Intelligence + Spawner */}
        <Route path="living-campaigns" element={
          <Suspense fallback={<PageFallback />}><LivingCampaigns /></Suspense>
        } />

        {/* Phase 7 — Multi-Channel-Orchestrator */}
        <Route path="multi-channel" element={
          <Suspense fallback={<PageFallback />}><MultiChannelPage /></Suspense>
        } />

        <Route path="dna" element={
          <ModuleGuard module="dna">
            <Suspense fallback={<PageFallback />}><DnaPage /></Suspense>
          </ModuleGuard>
        } />

        {/* Content-Modul (vereint Webseite + Blog + Medien + Anfragen) */}
        <Route path="content" element={
          <Suspense fallback={<PageFallback />}><Content /></Suspense>
        } />
        <Route path="content/page/:id" element={
          <Suspense fallback={<PageFallback />}><Pages /></Suspense>
        } />
        <Route path="content/post/:id" element={
          <ModuleGuard module="blog">
            <Suspense fallback={<PageFallback />}><BlogHub /></Suspense>
          </ModuleGuard>
        } />

        {/* Alte Routes (Redirects fuer Kompatibilitaet) */}
        <Route path="website" element={<Navigate to="/content?tab=seiten" replace />} />
        <Route path="website/:id" element={<Suspense fallback={<PageFallback />}><Pages /></Suspense>} />
        <Route path="blog" element={<Navigate to="/content?tab=blog" replace />} />
        <Route path="media" element={<Navigate to="/content?tab=medien" replace />} />
        <Route path="contact-submissions" element={
          <ModuleGuard module="crm">
            <Suspense fallback={<PageFallback />}><ContactSubmissions /></Suspense>
          </ModuleGuard>
        } />
        <Route path="content-plan" element={
          <ModuleGuard module="content_strategy">
            <Suspense fallback={<PageFallback />}><ContentPlan /></Suspense>
          </ModuleGuard>
        } />
        <Route path="content-strategy" element={
          <ModuleGuard module="content_strategy">
            <Suspense fallback={<PageFallback />}><ContentStrategy /></Suspense>
          </ModuleGuard>
        } />
        <Route path="ai" element={<Navigate to="/content" replace />} />
        <Route path="pages" element={<Navigate to="/content" replace />} />
        <Route path="aeo" element={<AEO />} />
        <Route path="listings" element={
          <ModuleGuard module="listings">
            <Suspense fallback={<PageFallback />}><Listings /></Suspense>
          </ModuleGuard>
        } />
        <Route path="llm-monitoring" element={<Navigate to="/aeo" replace />} />

        <Route path="seo" element={
          <ModuleGuard module="seo">
            <Suspense fallback={<PageFallback />}><SEO /></Suspense>
          </ModuleGuard>
        } />
        <Route path="keywords" element={
          <ModuleGuard module="keywords">
            <Suspense fallback={<PageFallback />}><Keywords /></Suspense>
          </ModuleGuard>
        } />
        <Route path="links" element={
          <ModuleGuard module="website">
            <Suspense fallback={<PageFallback />}><LinkHealth /></Suspense>
          </ModuleGuard>
        } />
        <Route path="clusters" element={
          <Suspense fallback={<PageFallback />}><Clusters /></Suspense>
        } />
        <Route path="cookie-banner" element={
          <ModuleGuard module="website">
            <Suspense fallback={<PageFallback />}><CookieBanner /></Suspense>
          </ModuleGuard>
        } />
        <Route path="ads" element={
          <ModuleGuard module="ads_base">
            <Suspense fallback={<PageFallback />}><AdsPage /></Suspense>
          </ModuleGuard>
        } />
        <Route path="ads/wizard/beginner" element={
          <ModuleGuard module="ads_base">
            <Suspense fallback={<PageFallback />}><AdsBeginnerWizard /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsPage /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/insights" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsInsights /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/radar" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdRadar /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/audiences" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsAudiences /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/pixel" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsPixel /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/leadforms" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsLeadForms /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/rules" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsRules /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/catalog" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsCatalog /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/messaging" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsMessaging /></Suspense>
          </ModuleGuard>
        } />
        <Route path="meta-ads/instagram-shopping" element={
          <ModuleGuard module="ads_meta">
            <Suspense fallback={<PageFallback />}><MetaAdsIgShopping /></Suspense>
          </ModuleGuard>
        } />
        {/* P0.2 (2026-05-17): Ads-LP-Studio — Cluster ausrollen */}
        <Route path="ads-lp" element={
          <ModuleGuard module="landing_pages">
            <Suspense fallback={<PageFallback />}><AdsLPClusters /></Suspense>
          </ModuleGuard>
        } />
        <Route path="cross-channel" element={
          <ModuleGuard module="cross_channel">
            <Suspense fallback={<PageFallback />}><CrossChannel /></Suspense>
          </ModuleGuard>
        } />
        <Route path="social-media" element={
          <ModuleGuard module="social_media">
            <Suspense fallback={<PageFallback />}><SocialMedia /></Suspense>
          </ModuleGuard>
        } />
        <Route path="newsletter" element={
          <ModuleGuard module="newsletter">
            <Suspense fallback={<PageFallback />}><Newsletter /></Suspense>
          </ModuleGuard>
        } />
        <Route path="analytics" element={
          <ModuleGuard module="analytics">
            <Suspense fallback={<PageFallback />}><Analytics /></Suspense>
          </ModuleGuard>
        } />
        <Route path="analytics/attribution" element={
          <ModuleGuard module="analytics">
            <Suspense fallback={<PageFallback />}><AttributionDashboard /></Suspense>
          </ModuleGuard>
        } />
        <Route path="content-package" element={
          <Suspense fallback={<PageFallback />}><ContentPackage /></Suspense>
        } />
        <Route path="quotes" element={<Suspense fallback={<PageFallback />}><Quotes /></Suspense>} />
        <Route path="brevo/kontakte" element={<Suspense fallback={<PageFallback />}><Kontakte /></Suspense>} />
        <Route path="academy" element={
          <ModuleGuard module="academy">
            <Suspense fallback={<PageFallback />}><MarketingAcademy /></Suspense>
          </ModuleGuard>
        } />
        <Route path="brand" element={<Suspense fallback={<PageFallback />}><BrandHub /></Suspense>} />
        <Route path="system" element={<Suspense fallback={<PageFallback />}><SystemPage /></Suspense>} />
        <Route path="onboarding" element={<Suspense fallback={<PageFallback />}><Onboarding /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageFallback />}><SettingsPage /></Suspense>} />
        {/* Google-Ads OAuth-Callback — selbe Settings-Page, aber dieser Path-Trigger
            wird im ApiTab.useEffect erkannt um den Ads-Callback statt GSC zu fahren. */}
        <Route path="settings/ads-oauth-callback" element={<Suspense fallback={<PageFallback />}><SettingsPage /></Suspense>} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
